import ConstructiveButton from '@/features/common/buttons/ConstructiveButton'
import { Layout } from '@/features/layout/types'
import ContextMenu from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/PaymentActions/parts/ContextMenu'
import PaymentTypeChips from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/PaymentTypeChips'
import {
    actionsStylesheet,
    iconButtonSize,
} from '@/features/pos/components/OrderFinalization/parts/BottomSheet/styles'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import IconButton from '@/features/ui/buttons/components/IconButton'
import CloseIcon from '@/features/ui/icons/components/CloseIcon'
import { FC, useEffect, useState } from 'react'
import { View } from 'react-native'
import { useStyles } from 'react-native-unistyles'

interface PaymentActionsProps {
    layout: Layout
    paymentTypes: PaymentType[]
    actionDisabled: boolean
    actionLabel: string
    onAction: (paymentType: PaymentType) => Promise<void>
    onCancel: () => void
    onInsertNote: () => void
}

const PaymentActions: FC<PaymentActionsProps> = ({
    layout,
    paymentTypes,
    actionDisabled,
    actionLabel,
    onAction,
    onCancel,
    onInsertNote,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null)

    const { styles } = useStyles(actionsStylesheet, { layout })

    useEffect(() => {
        if (!paymentTypes.length) {
            setSelectedPaymentType(null)
            return
        }

        if (selectedPaymentType && paymentTypes.includes(selectedPaymentType)) return

        const nextPaymentType = paymentTypes[0] ?? null
        setSelectedPaymentType(nextPaymentType)
    }, [paymentTypes, selectedPaymentType])

    const handleAction = async () => {
        if (!selectedPaymentType) return

        try {
            setIsLoading(true)
            await onAction(selectedPaymentType)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <View style={styles.container}>
            <PaymentTypeChips
                layout={layout}
                paymentTypes={paymentTypes}
                selectedPaymentType={selectedPaymentType}
                disabled={isLoading}
                onSelect={setSelectedPaymentType}
            />
            <View style={styles.actionsRow}>
                <IconButton
                    Icon={CloseIcon}
                    type="filled"
                    color="primary"
                    size={iconButtonSize}
                    onPress={onCancel}
                    style={styles.closeButton}
                />
                <ConstructiveButton
                    disabled={actionDisabled || !selectedPaymentType || isLoading}
                    loading={isLoading}
                    onPress={handleAction}
                    style={styles.actionButton}
                    textStyle={styles.actionButtonText}
                >
                    {actionLabel}
                </ConstructiveButton>
                <ContextMenu onInsertNote={onInsertNote} />
            </View>
        </View>
    )
}

export default PaymentActions
